import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { greenCoffeeLotsApi, selectionLotsApi, suppliersApi, warehousesApi } from '../../../services/api'
import StatusBadge from './StatusBadge'
import TableActionButton from '../../../components/TableActionButton'
import SortableTh from '../../../components/SortableTh'
import { useSortableData } from '../../../hooks/useSortableData'

interface Warehouse {
  id: number
  name: string
  is_active: boolean
}

interface Supplier {
  id: number
  name: string
  is_active: boolean
}

interface GreenCoffeeLot {
  id: number
  lot_code: string
  kg_remaining: number
  status: string
}

interface SelectionLot {
  id: number
  lot_code: string
  source_lot_id: number | null
  source_lot_code: string | null
  quality_grade: number
  kg_selected: number
  kg_remaining: number
  allocated_cost: number
  cost_per_kg: number
  status: string
  warehouse_id: number
  warehouse_name: string
  origin_warehouse_id: number | null
  transit_eta: string | null
  selection_date: string
  notes: string | null
  is_active: boolean
  supplier_id: number | null
  supplier_name: string | null
  direct_price_per_kg: number | null
  direct_assembly_cost: number | null
  direct_freight_cost: number | null
  direct_storage_cost: number | null
}

interface SplitDraft {
  quality_grade: string
  kg_selected: string
  warehouse_id: string
  status: 'at_rest' | 'in_transit'
  origin_warehouse_id: string
  transit_eta: string
  notes: string
}

const emptySplit: SplitDraft = {
  quality_grade: '1',
  kg_selected: '',
  warehouse_id: '',
  status: 'at_rest',
  origin_warehouse_id: '',
  transit_eta: '',
  notes: '',
}

const gradeLabels: Record<string, string> = {
  '1': '1 · Premium',
  '2': '2 · Medio',
  '3': '3 · Granel',
  '4': '4 · Granel',
  '5': '5 · Granel',
}

interface EditForm {
  warehouse_id: string
  origin_warehouse_id: string
  transit_eta: string
  status: 'at_rest' | 'in_transit'
  notes: string
  kg_selected: string
  price_per_kg: string
  assembly_cost: string
  freight_cost: string
  storage_cost: string
}

interface DirectForm {
  quality_grade: string
  kg_purchased: string
  price_per_kg: string
  assembly_cost: string
  freight_cost: string
  storage_cost: string
  supplier_id: string
  warehouse_id: string
  status: 'at_rest' | 'in_transit'
  origin_warehouse_id: string
  transit_eta: string
  notes: string
}

const emptyDirectForm: DirectForm = {
  quality_grade: '1',
  kg_purchased: '',
  price_per_kg: '',
  assembly_cost: '',
  freight_cost: '',
  storage_cost: '',
  supplier_id: '',
  warehouse_id: '',
  status: 'at_rest',
  origin_warehouse_id: '',
  transit_eta: '',
  notes: '',
}

export default function SelectionTab() {
  const [selectionLots, setSelectionLots] = useState<SelectionLot[]>([])
  const [sourceLots, setSourceLots] = useState<GreenCoffeeLot[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [createMode, setCreateMode] = useState<'chain' | 'direct'>('chain')
  const [sourceLotId, setSourceLotId] = useState('')
  const [splitDraft, setSplitDraft] = useState<SplitDraft>(emptySplit)
  const [cart, setCart] = useState<SplitDraft[]>([])
  const [directForm, setDirectForm] = useState<DirectForm>(emptyDirectForm)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const [editingLot, setEditingLot] = useState<SelectionLot | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({
    warehouse_id: '',
    origin_warehouse_id: '',
    transit_eta: '',
    status: 'at_rest',
    notes: '',
    kg_selected: '',
    price_per_kg: '',
    assembly_cost: '',
    freight_cost: '',
    storage_cost: '',
  })
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [cancelingId, setCancelingId] = useState<number | null>(null)

  const fetchAll = async () => {
    try {
      setLoadError('')
      const [selRes, sourceRes, whRes, suppliersRes] = await Promise.all([
        selectionLotsApi.list(),
        greenCoffeeLotsApi.list({ status: 'at_rest' }),
        warehousesApi.list(),
        suppliersApi.list(),
      ])
      setSelectionLots(selRes.data.lots || [])
      setSourceLots((sourceRes.data.lots || []).filter((l: GreenCoffeeLot) => l.kg_remaining > 0))
      setWarehouses(whRes.data.warehouses || [])
      setSuppliers(suppliersRes.data.suppliers || [])
    } catch (err) {
      console.error('Error fetching selection lots:', err)
      setLoadError('No se pudieron cargar los lotes de selección. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const activeWarehouses = useMemo(() => warehouses.filter((w) => w.is_active), [warehouses])

  const filteredSelectionLots = useMemo(() => {
    if (!statusFilter) return selectionLots
    return selectionLots.filter((l) => l.status === statusFilter)
  }, [selectionLots, statusFilter])

  const { sortedData: sortedSelectionLots, sortConfig, requestSort } = useSortableData(filteredSelectionLots)

  const selectedSourceLot = useMemo(
    () => sourceLots.find((l) => String(l.id) === sourceLotId),
    [sourceLots, sourceLotId]
  )

  const kgAllocated = useMemo(
    () => cart.reduce((sum, s) => sum + (parseFloat(s.kg_selected) || 0), 0),
    [cart]
  )
  const kgAvailable = selectedSourceLot ? Number(selectedSourceLot.kg_remaining) : 0
  const kgLeft = kgAvailable - kgAllocated

  const openNew = () => {
    setCreateMode('chain')
    setSourceLotId('')
    setSplitDraft(emptySplit)
    setCart([])
    setDirectForm(emptyDirectForm)
    setError('')
    setShowForm(true)
  }

  const handleSplitChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setSplitDraft({ ...splitDraft, [e.target.name]: e.target.value })
  }

  const handleDirectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setDirectForm({ ...directForm, [e.target.name]: e.target.value })
  }

  const handleAddSplit = () => {
    if (!splitDraft.warehouse_id) {
      setError('Selecciona el almacén de destino para esta división')
      return
    }
    const kg = parseFloat(splitDraft.kg_selected)
    if (!kg || kg <= 0) {
      setError('Ingresa un peso válido para esta división')
      return
    }
    if (kg > kgLeft) {
      setError(`Solo quedan ${kgLeft.toFixed(2)} kg disponibles de este lote`)
      return
    }
    if (splitDraft.status === 'in_transit' && !splitDraft.origin_warehouse_id) {
      setError('Selecciona el almacén de origen para una división en tránsito')
      return
    }
    setCart((prev) => [...prev, splitDraft])
    setSplitDraft({ ...emptySplit, warehouse_id: splitDraft.warehouse_id })
    setError('')
  }

  const handleRemoveSplit = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (createMode === 'direct') {
      const kg = parseFloat(directForm.kg_purchased)
      if (!kg || kg <= 0) {
        setError('Ingresa un peso comprado válido')
        return
      }
      if (!directForm.warehouse_id) {
        setError('Selecciona un almacén')
        return
      }
      if (directForm.status === 'in_transit' && !directForm.origin_warehouse_id) {
        setError('Selecciona el almacén de origen para una compra en tránsito')
        return
      }
      setSaving(true)
      setError('')
      try {
        await selectionLotsApi.create({
          quality_grade: Number(directForm.quality_grade),
          kg_purchased: kg,
          price_per_kg: directForm.price_per_kg ? parseFloat(directForm.price_per_kg) : undefined,
          assembly_cost: directForm.assembly_cost ? parseFloat(directForm.assembly_cost) : undefined,
          freight_cost: directForm.freight_cost ? parseFloat(directForm.freight_cost) : undefined,
          storage_cost: directForm.storage_cost ? parseFloat(directForm.storage_cost) : undefined,
          supplier_id: directForm.supplier_id ? Number(directForm.supplier_id) : undefined,
          warehouse_id: Number(directForm.warehouse_id),
          status: directForm.status,
          origin_warehouse_id:
            directForm.status === 'in_transit' && directForm.origin_warehouse_id
              ? Number(directForm.origin_warehouse_id)
              : undefined,
          transit_eta: directForm.status === 'in_transit' && directForm.transit_eta ? directForm.transit_eta : undefined,
          notes: directForm.notes || undefined,
        })
        toast.success('Compra directa de selección registrada')
        setShowForm(false)
        fetchAll()
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al registrar la compra directa')
      } finally {
        setSaving(false)
      }
      return
    }
    if (!sourceLotId) {
      setError('Selecciona el lote de café verde de origen')
      return
    }
    if (cart.length === 0) {
      setError('Agrega al menos una división de calidad')
      return
    }
    setSaving(true)
    setError('')
    try {
      await selectionLotsApi.create({
        source_lot_id: Number(sourceLotId),
        splits: cart.map((s) => ({
          quality_grade: Number(s.quality_grade),
          kg_selected: parseFloat(s.kg_selected),
          warehouse_id: Number(s.warehouse_id),
          status: s.status,
          origin_warehouse_id: s.status === 'in_transit' && s.origin_warehouse_id ? Number(s.origin_warehouse_id) : undefined,
          transit_eta: s.status === 'in_transit' && s.transit_eta ? s.transit_eta : undefined,
          notes: s.notes || undefined,
        })),
      })
      toast.success('Selección registrada')
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al registrar la selección')
    } finally {
      setSaving(false)
    }
  }

  const openEdit = (lot: SelectionLot) => {
    setEditingLot(lot)
    setEditForm({
      warehouse_id: String(lot.warehouse_id),
      origin_warehouse_id: lot.origin_warehouse_id ? String(lot.origin_warehouse_id) : '',
      transit_eta: lot.transit_eta || '',
      status: lot.status === 'in_transit' ? 'in_transit' : 'at_rest',
      notes: lot.notes || '',
      kg_selected: String(lot.kg_selected),
      price_per_kg: lot.direct_price_per_kg !== null ? String(lot.direct_price_per_kg) : '',
      assembly_cost: lot.direct_assembly_cost !== null ? String(lot.direct_assembly_cost) : '',
      freight_cost: lot.direct_freight_cost !== null ? String(lot.direct_freight_cost) : '',
      storage_cost: lot.direct_storage_cost !== null ? String(lot.direct_storage_cost) : '',
    })
    setEditError('')
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingLot) return
    const kg = parseFloat(editForm.kg_selected)
    if (!kg || kg <= 0) {
      setEditError('Ingresa un peso seleccionado válido')
      return
    }
    setEditSaving(true)
    setEditError('')
    try {
      const payload: any = {
        warehouse_id: Number(editForm.warehouse_id),
        origin_warehouse_id: editForm.origin_warehouse_id ? Number(editForm.origin_warehouse_id) : null,
        transit_eta: editForm.transit_eta || null,
        status: editForm.status,
        notes: editForm.notes || null,
        kg_selected: kg,
      }
      if (editingLot.source_lot_id === null) {
        payload.direct_price_per_kg = parseFloat(editForm.price_per_kg) || 0
        payload.direct_assembly_cost = parseFloat(editForm.assembly_cost) || 0
        payload.direct_freight_cost = parseFloat(editForm.freight_cost) || 0
        payload.direct_storage_cost = parseFloat(editForm.storage_cost) || 0
      }
      await selectionLotsApi.update(editingLot.id, payload)
      toast.success('Lote de selección actualizado')
      setEditingLot(null)
      fetchAll()
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Error al actualizar el lote')
    } finally {
      setEditSaving(false)
    }
  }

  const handleQuickMarkAtRest = async (lot: SelectionLot) => {
    try {
      await selectionLotsApi.update(lot.id, { status: 'at_rest' })
      toast.success('Lote marcado en almacén')
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al actualizar el lote')
    }
  }

  const handleCancel = async (lot: SelectionLot) => {
    if (!confirm(`¿Anular el lote de selección ${lot.lot_code}? Esta acción restaurará los kilos reservados en la etapa anterior.`)) return
    setCancelingId(lot.id)
    try {
      await selectionLotsApi.cancel(lot.id)
      toast.success('Lote de selección anulado')
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al anular el lote')
    } finally {
      setCancelingId(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex items-center gap-3">
          <label htmlFor="selection-status-filter" className="text-xs font-medium text-espresso-500">
            Estado
          </label>
          <select
            id="selection-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          >
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="in_transit">En Tránsito</option>
            <option value="at_rest">En Almacén</option>
            <option value="consumed">Consumido</option>
          </select>
        </div>
        <button
          onClick={openNew}
          disabled={sourceLots.length === 0 || activeWarehouses.length === 0}
          title={
            sourceLots.length === 0
              ? 'No hay lotes de café verde en almacén disponibles para seleccionar'
              : activeWarehouses.length === 0
              ? 'Crea primero un almacén activo'
              : undefined
          }
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nueva Selección
        </button>
      </div>

      {sourceLots.length === 0 && (
        <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          No hay lotes de café verde en almacén con kg disponibles. Recibe una compra primero en la pestaña "Compras".
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && filteredSelectionLots.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">
            {selectionLots.length === 0 ? 'No hay lotes de selección registrados aún.' : 'No hay lotes con este estado.'}
          </p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Lote" sortKey="lot_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Fecha" sortKey="selection_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Origen" sortKey="source_lot_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Calidad" sortKey="quality_grade" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Almacén" sortKey="warehouse_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Sel." sortKey="kg_selected" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Rest." sortKey="kg_remaining" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo/kg" sortKey="cost_per_kg" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedSelectionLots.map((lot) => (
                  <tr key={lot.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{lot.lot_code}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {lot.selection_date ? new Date(lot.selection_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {lot.source_lot_code || `Compra directa${lot.supplier_name ? ` — ${lot.supplier_name}` : ''}`}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {gradeLabels[String(lot.quality_grade)] || lot.quality_grade}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{lot.warehouse_name}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(lot.kg_selected).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(lot.kg_remaining).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">${Number(lot.cost_per_kg).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap"><StatusBadge status={lot.status} isActive={lot.is_active} /></td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        {lot.status !== 'consumed' && (
                          <TableActionButton onClick={() => openEdit(lot)} variant="primary">
                            Editar
                          </TableActionButton>
                        )}
                        {lot.status === 'in_transit' && (
                          <TableActionButton onClick={() => handleQuickMarkAtRest(lot)} variant="success">
                            Marcar en almacén
                          </TableActionButton>
                        )}
                        {lot.is_active !== false && (
                          <TableActionButton
                            onClick={() => handleCancel(lot)}
                            variant="danger"
                            disabled={cancelingId === lot.id || Number(lot.kg_remaining) !== Number(lot.kg_selected)}
                            title={
                              Number(lot.kg_remaining) !== Number(lot.kg_selected)
                                ? 'No se puede anular: ya se generaron lotes de tostado a partir de este lote'
                                : undefined
                            }
                          >
                            {cancelingId === lot.id ? 'Anulando...' : 'Anular'}
                          </TableActionButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal (multi-split cart) */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Nueva Selección de Calidad</h2>
              {createMode === 'chain' && (
                <p className="text-sm text-espresso-500 mt-1">
                  Divide un lote de café verde en hasta 5 lotes según calidad (Premium, Medio, Granel).
                </p>
              )}
            </div>
            <div className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}

              <div className="flex rounded-md overflow-hidden border border-espresso-300 w-fit">
                <button
                  type="button"
                  onClick={() => setCreateMode('chain')}
                  className={`px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
                    createMode === 'chain' ? 'bg-terracotta-600 text-white' : 'bg-cream-50 text-espresso-700 hover:bg-cream-100'
                  }`}
                >
                  Producido internamente
                </button>
                <button
                  type="button"
                  onClick={() => setCreateMode('direct')}
                  className={`px-4 py-2 text-sm font-medium border-l border-espresso-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
                    createMode === 'direct' ? 'bg-terracotta-600 text-white' : 'bg-cream-50 text-espresso-700 hover:bg-cream-100'
                  }`}
                >
                  Compra directa
                </button>
              </div>

              {createMode === 'chain' && (
                <div>
                  <label htmlFor="source-lot" className="block text-sm font-medium text-espresso-700 mb-1">
                    Lote de café verde de origen *
                  </label>
                  <select
                    id="source-lot"
                    value={sourceLotId}
                    onChange={(e) => {
                      setSourceLotId(e.target.value)
                      setCart([])
                    }}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="">Selecciona un lote</option>
                    {sourceLots.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.lot_code} · {Number(l.kg_remaining).toFixed(2)} kg disponibles
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {createMode === 'chain' && sourceLotId && (
                <>
                  <div className="bg-cream-50 rounded-md p-3 text-sm flex justify-between">
                    <span>Disponible: {kgAvailable.toFixed(2)} kg</span>
                    <span>Asignado: {kgAllocated.toFixed(2)} kg</span>
                    <span className="font-semibold">Restante: {kgLeft.toFixed(2)} kg</span>
                  </div>

                  <div className="border border-espresso-200 rounded-md p-4 space-y-3">
                    <h3 className="text-sm font-medium text-espresso-700">Agregar división</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="split-grade" className="block text-xs font-medium text-espresso-500 mb-1">
                          Calidad
                        </label>
                        <select
                          id="split-grade"
                          name="quality_grade"
                          value={splitDraft.quality_grade}
                          onChange={handleSplitChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          {Object.entries(gradeLabels).map(([v, label]) => (
                            <option key={v} value={v}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="split-kg" className="block text-xs font-medium text-espresso-500 mb-1">
                          Kg a seleccionar
                        </label>
                        <input
                          id="split-kg"
                          type="number"
                          name="kg_selected"
                          value={splitDraft.kg_selected}
                          onChange={handleSplitChange}
                          step="0.01"
                          min="0"
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="split-warehouse" className="block text-xs font-medium text-espresso-500 mb-1">
                          Almacén destino
                        </label>
                        <select
                          id="split-warehouse"
                          name="warehouse_id"
                          value={splitDraft.warehouse_id}
                          onChange={handleSplitChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="">Selecciona</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id} disabled={!w.is_active}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="split-status" className="block text-xs font-medium text-espresso-500 mb-1">
                          Estado
                        </label>
                        <select
                          id="split-status"
                          name="status"
                          value={splitDraft.status}
                          onChange={handleSplitChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="at_rest">En Almacén</option>
                          <option value="in_transit">En Tránsito</option>
                        </select>
                      </div>
                      {splitDraft.status === 'in_transit' && (
                        <>
                          <div>
                            <label htmlFor="split-origin" className="block text-xs font-medium text-espresso-500 mb-1">
                              Almacén origen
                            </label>
                            <select
                              id="split-origin"
                              name="origin_warehouse_id"
                              value={splitDraft.origin_warehouse_id}
                              onChange={handleSplitChange}
                              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                            >
                              <option value="">Selecciona</option>
                              {warehouses.map((w) => (
                                <option key={w.id} value={w.id}>
                                  {w.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="split-eta" className="block text-xs font-medium text-espresso-500 mb-1">
                              ETA
                            </label>
                            <input
                              id="split-eta"
                              type="date"
                              name="transit_eta"
                              value={splitDraft.transit_eta}
                              onChange={handleSplitChange}
                              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                            />
                          </div>
                        </>
                      )}
                      <div className="col-span-2">
                        <label htmlFor="split-notes" className="block text-xs font-medium text-espresso-500 mb-1">
                          Notas
                        </label>
                        <input
                          id="split-notes"
                          type="text"
                          name="notes"
                          value={splitDraft.notes}
                          onChange={handleSplitChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddSplit}
                      className="w-full px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700"
                    >
                      + Agregar División
                    </button>
                  </div>

                  {cart.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-espresso-700">Divisiones agregadas ({cart.length})</h4>
                      {cart.map((s, index) => (
                        <div key={index} className="flex items-center justify-between bg-cream-50 rounded-md p-3 text-sm">
                          <div>
                            <div className="font-medium text-espresso-800">
                              {gradeLabels[s.quality_grade]} · {parseFloat(s.kg_selected).toFixed(2)} kg
                            </div>
                            <div className="text-xs text-espresso-500">
                              {warehouses.find((w) => String(w.id) === s.warehouse_id)?.name} ·{' '}
                              {s.status === 'in_transit' ? 'En Tránsito' : 'En Almacén'}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveSplit(index)}
                            className="text-red-600 hover:text-red-900 text-xs"
                          >
                            Quitar
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {createMode === 'direct' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="direct-grade" className="block text-sm font-medium text-espresso-700 mb-1">
                        Calidad *
                      </label>
                      <select
                        id="direct-grade"
                        name="quality_grade"
                        value={directForm.quality_grade}
                        onChange={handleDirectChange}
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      >
                        {Object.entries(gradeLabels).map(([v, label]) => (
                          <option key={v} value={v}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="direct-kg" className="block text-sm font-medium text-espresso-700 mb-1">
                        Kg Comprados *
                      </label>
                      <input
                        id="direct-kg"
                        type="number"
                        name="kg_purchased"
                        value={directForm.kg_purchased}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="direct-price" className="block text-sm font-medium text-espresso-700 mb-1">
                        Precio / kg
                      </label>
                      <input
                        id="direct-price"
                        type="number"
                        name="price_per_kg"
                        value={directForm.price_per_kg}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="direct-assembly" className="block text-sm font-medium text-espresso-700 mb-1">
                        Acopio
                      </label>
                      <input
                        id="direct-assembly"
                        type="number"
                        name="assembly_cost"
                        value={directForm.assembly_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="direct-freight" className="block text-sm font-medium text-espresso-700 mb-1">
                        Flete
                      </label>
                      <input
                        id="direct-freight"
                        type="number"
                        name="freight_cost"
                        value={directForm.freight_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="direct-storage" className="block text-sm font-medium text-espresso-700 mb-1">
                        Almacenaje
                      </label>
                      <input
                        id="direct-storage"
                        type="number"
                        name="storage_cost"
                        value={directForm.storage_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="direct-supplier" className="block text-sm font-medium text-espresso-700 mb-1">
                      Proveedor
                    </label>
                    <select
                      id="direct-supplier"
                      name="supplier_id"
                      value={directForm.supplier_id}
                      onChange={handleDirectChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="">Sin proveedor</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id} disabled={!s.is_active}>
                          {s.name}{!s.is_active ? ' (inactivo)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="direct-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                        Almacén destino *
                      </label>
                      <select
                        id="direct-warehouse"
                        name="warehouse_id"
                        value={directForm.warehouse_id}
                        onChange={handleDirectChange}
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      >
                        <option value="">Selecciona un almacén</option>
                        {warehouses.map((w) => (
                          <option key={w.id} value={w.id} disabled={!w.is_active}>
                            {w.name}{!w.is_active ? ' (inactivo)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="direct-status" className="block text-sm font-medium text-espresso-700 mb-1">
                        Estado
                      </label>
                      <select
                        id="direct-status"
                        name="status"
                        value={directForm.status}
                        onChange={handleDirectChange}
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      >
                        <option value="at_rest">En Almacén</option>
                        <option value="in_transit">En Tránsito</option>
                      </select>
                    </div>
                  </div>
                  {directForm.status === 'in_transit' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="direct-origin" className="block text-sm font-medium text-espresso-700 mb-1">
                          Almacén origen
                        </label>
                        <select
                          id="direct-origin"
                          name="origin_warehouse_id"
                          value={directForm.origin_warehouse_id}
                          onChange={handleDirectChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="">Selecciona</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="direct-eta" className="block text-sm font-medium text-espresso-700 mb-1">
                          ETA
                        </label>
                        <input
                          id="direct-eta"
                          type="date"
                          name="transit_eta"
                          value={directForm.transit_eta}
                          onChange={handleDirectChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label htmlFor="direct-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                      Notas
                    </label>
                    <textarea
                      id="direct-notes"
                      name="notes"
                      value={directForm.notes}
                      onChange={handleDirectChange}
                      rows={2}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving || (createMode === 'chain' && cart.length === 0)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : createMode === 'chain' ? 'Confirmar Selección' : 'Registrar Compra Directa'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit (logistics only) Modal */}
      {editingLot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Editar Lote {editingLot.lot_code}</h2>
              <p className="text-sm text-espresso-500 mt-1">
                Puedes editar el peso seleccionado y los datos logísticos. La calidad no se puede modificar.
              </p>
            </div>
            <form onSubmit={handleEditSubmit} className="px-6 py-4 space-y-4">
              {editError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {editError}
                </div>
              )}
              <div>
                <label htmlFor="edit-kg-selected" className="block text-sm font-medium text-espresso-700 mb-1">
                  Kg seleccionados *
                </label>
                <input
                  id="edit-kg-selected"
                  type="number"
                  value={editForm.kg_selected}
                  onChange={(e) => setEditForm({ ...editForm, kg_selected: e.target.value })}
                  step="0.01"
                  min="0"
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              {editingLot.source_lot_id === null && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="edit-price" className="block text-sm font-medium text-espresso-700 mb-1">
                        Precio / kg
                      </label>
                      <input
                        id="edit-price"
                        type="number"
                        value={editForm.price_per_kg}
                        onChange={(e) => setEditForm({ ...editForm, price_per_kg: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="edit-assembly" className="block text-sm font-medium text-espresso-700 mb-1">
                        Acopio
                      </label>
                      <input
                        id="edit-assembly"
                        type="number"
                        value={editForm.assembly_cost}
                        onChange={(e) => setEditForm({ ...editForm, assembly_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-freight" className="block text-sm font-medium text-espresso-700 mb-1">
                        Flete
                      </label>
                      <input
                        id="edit-freight"
                        type="number"
                        value={editForm.freight_cost}
                        onChange={(e) => setEditForm({ ...editForm, freight_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-storage" className="block text-sm font-medium text-espresso-700 mb-1">
                        Almacenaje
                      </label>
                      <input
                        id="edit-storage"
                        type="number"
                        value={editForm.storage_cost}
                        onChange={(e) => setEditForm({ ...editForm, storage_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                <label htmlFor="edit-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                  Almacén
                </label>
                <select
                  id="edit-warehouse"
                  value={editForm.warehouse_id}
                  onChange={(e) => setEditForm({ ...editForm, warehouse_id: e.target.value })}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="edit-status" className="block text-sm font-medium text-espresso-700 mb-1">
                  Estado
                </label>
                <select
                  id="edit-status"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'at_rest' | 'in_transit' })}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                >
                  <option value="at_rest">En Almacén</option>
                  <option value="in_transit">En Tránsito</option>
                </select>
              </div>
              {editForm.status === 'in_transit' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="edit-origin" className="block text-sm font-medium text-espresso-700 mb-1">
                      Almacén origen
                    </label>
                    <select
                      id="edit-origin"
                      value={editForm.origin_warehouse_id}
                      onChange={(e) => setEditForm({ ...editForm, origin_warehouse_id: e.target.value })}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="">Selecciona</option>
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="edit-eta" className="block text-sm font-medium text-espresso-700 mb-1">
                      ETA
                    </label>
                    <input
                      id="edit-eta"
                      type="date"
                      value={editForm.transit_eta}
                      onChange={(e) => setEditForm({ ...editForm, transit_eta: e.target.value })}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="edit-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="edit-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setEditingLot(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {editSaving ? 'Guardando...' : 'Actualizar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
